// SERVERDAGI EKSPORT — panel tomonidagi yagona kirish nuqtasi.
//
// ⚠️ NEGA UMUMAN KERAK BO'LDI
// Backendda `uz.xsib.common.export` bitta dvigatel sifatida yozilgan:
// CSV, XLSX, JSON, oqim bilan, 200 000 qator chegarasi va doira
// ekrani AYNI kodda. Panel esa CSV'ni BRAUZERDA, allaqachon
// yuklangan sahifadan yasardi (`shared/lib/download.ts`). Uch oqibati
// bor edi va uchalasi ham jimgina:
//
//   1) XLSX umuman yo'q edi — dvigatel uni beradi, panel so'ramasdi;
//   2) filtrlar serverga bormasdi — faylga faqat EKRANDAGI sahifa
//      tushardi, ya'ni "butun ro'yxatni Excel'ga chiqar" ishlamasdi;
//   3) katta jadvalni brauzerda formatlash sahifani qotirardi.
//
// Brauzerdagi yo'l OLIB TASHLANMADI: kichik va sahifaga xos ro'yxatlar
// (masalan bitta partiyaning kodlari) uchun u to'g'ri va tezroq.
// Bu modul FAQAT server yo'lini boshqaradi.

import axios from 'axios'
import { api } from '@/shared/api/client'
import { translateError, type Translate } from '@/shared/api/errors'
import { downloadBlob } from './download'

/**
 * Panel so'ray oladigan formatlar — backend `ExportFormat` enum'ining
 * `slug()` qiymatlari bilan AYNAN bir xil.
 *
 * ⚠️ Bu ro'yxat backenddan KO'CHIRMA, ya'ni ajralib ketishi mumkin.
 * `tests/export.mjs` §1 uni Java manbasi bilan solishtiradi
 * (`guards.mjs` §7 naqshi).
 */
export const EXPORT_FORMATS = ['csv', 'xlsx', 'json'] as const
export type ExportFormat = (typeof EXPORT_FORMATS)[number]

/**
 * Bitta faylga tushadigan eng ko'p qator — backend `ExportLimits.MAX_ROWS`.
 *
 * ⚠️ Server chegaraga yetganda faylni KESADI va ogohlantirishni faylning
 * ICHIGA yozadi. Ya'ni operator buni fayl ochilgandan keyin biladi —
 * yuz mingta qator kutib o'tirgandan KEYIN. Shuning uchun panel buni
 * OLDINDAN aytadi (`ServerExportButton`), bu chegarani bu yerdan oladi.
 */
export const EXPORT_MAX_ROWS = 200_000

/**
 * ⚠️ PDF ATAYLAB YO'Q va bu vaqtinchalik kamchilik emas.
 *
 * Sabablari backend `ExportFormats` javadoc'ida o'lchangan holda
 * yozilgan: iText yadrosi AGPL (ya'ni SaaS'da butun manba kodni ochish
 * talabi), bepul muqobillari esa arab yozuvini SHAKLLANTIRMAYDI —
 * harflar bir-biriga ulanmaydi va o'ngdan chapga tartib buziladi.
 * Tenant esa `Asia/Kabul`, ya'ni hujjatlar aynan shu yozuvda.
 *
 * Panel bu formatni MENYUDA ko'rsatadi, lekin o'chirilgan holda va
 * sababi bilan — "yo'q" degan javobni operator tugmani bosishdan OLDIN
 * olsin. Bosib qo'ysa ham (masalan havola orqali) quyidagi
 * {@link ExportFormatError} aniq sababni beradi.
 */
export const UNAVAILABLE_FORMATS = ['pdf'] as const

/**
 * Panel so'ramaydigan formatga urinish.
 *
 * ⚠️ `name` ATAYLAB barqaror satr: `errors.ts` dagi `XsibApiError` bilan
 * bir xil sabab — modul aylanasini yaratmasdan tanib olish uchun.
 */
export class ExportFormatError extends Error {
  constructor(readonly requested: string) {
    super(`Eksport formati qo'llab-quvvatlanmaydi: ${requested}`)
    this.name = 'XsibExportFormatError'
  }
}

export function isExportFormat(value: unknown): value is ExportFormat {
  return typeof value === 'string' && (EXPORT_FORMATS as readonly string[]).includes(value)
}

/** Noma'lum format uchun `null` — chaqiruvchi o'zi qaror qiladi */
export function parseExportFormat(value: string | null | undefined): ExportFormat | null {
  if (value === null || value === undefined) return null
  return isExportFormat(value.toLowerCase()) ? (value.toLowerCase() as ExportFormat) : null
}

/**
 * `Content-Disposition` dan fayl nomi.
 *
 * ⚠️ `filename*` USTUN turadi (RFC 6266): backend ikkalasini ham yuboradi,
 * `filename=` esa ASCII'ga tushirilgan zaxira nusxa. Kirill yoki o'zbek
 * harfli nom faqat `filename*` da to'g'ri keladi.
 *
 * Sarlavha umuman yetib kelmasligi mumkin — ba'zi proksi uni kesadi.
 * Shunda zaxira nom ishlatiladi, yuklab olish esa TO'XTAMAYDI.
 */
export function filenameFromDisposition(header: string | undefined, fallback: string): string {
  const raw = String(header ?? '')
  const encoded = /filename\*=\s*UTF-8''([^;]+)/i.exec(raw)
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim())
    } catch {
      // Buzuq foizli kodlash — pastdagi oddiy shaklga tushamiz
    }
  }
  const plain = /filename=\s*"?([^";]+)"?/i.exec(raw)
  return plain?.[1]?.trim() ?? fallback
}

/**
 * Blob ichida kelgan XATO tanasini o'qib, axios xatosining ichiga
 * qaytarib qo'yadi.
 *
 * <h2>⚠️ Nega bu SHART</h2>
 * `responseType: 'blob'` bilan axios XATO javobini ham Blob qiladi.
 * Ya'ni `translateError` `body.error.text` ni ko'ra olmaydi va har
 * qanday rad javobi ekranda «Xato (400)» bo'lib chiqadi — aynan
 * "nimadir xato bo'ldi" turidagi foydasiz xabar. Server esa sababni
 * TO'LIQ yozib yuborgan bo'ladi ("PDF hozircha yo'q — ...").
 *
 * Tanani o'qib, `error.response.data` ni oddiy obyektga almashtiramiz —
 * shundan keyin butun panel bo'ylab bitta `translateError` ishlaydi.
 *
 * ⚠️ SATR HOLATI HAM QAMRAB OLINGAN. Brauzerda (XHR adapteri) tana
 * `Blob` bo'lib keladi, Node'da esa (sinovlar shu yerda yuradi) axios
 * `responseType: 'blob'` ni qo'llab-quvvatlamaydi va oddiy SATR beradi.
 * Ikkala shakl ham shu yerda ochiladi — shart bitta joyda qoladi va
 * sinov brauzerdagi bilan bir xil yo'ldan yuradi.
 */
export async function unwrapBlobError(error: unknown): Promise<unknown> {
  if (!axios.isAxiosError(error)) return error
  const body: unknown = error.response?.data
  if (!(body instanceof Blob) && typeof body !== 'string') return error
  try {
    const text = body instanceof Blob ? await body.text() : body
    if (text.trim() !== '') {
      error.response!.data = JSON.parse(text) as unknown
    }
  } catch {
    // JSON emas (HTML xato sahifasi, bo'sh tana) — asl xato o'z holicha
    // qoladi va `translateError` HTTP kodi bo'yicha xabar beradi.
  }
  return error
}

export interface ServerExportRequest {
  /** `/api/reports/export/revenue` kabi to'liq yo'l */
  path: string
  format: string
  /** Formatdan tashqari so'rov parametrlari — `undefined` lar tushib qoladi */
  params?: Record<string, string | number | undefined | null>
  /** Sarlavha yetib kelmasa ishlatiladigan nom (kengaytmasiz) */
  fallbackBaseName: string
  /** Yuklab olingan baytlar — jarayonni ko'rsatish uchun */
  onProgress?: (loadedBytes: number, totalBytes: number | null) => void
  signal?: AbortSignal
}

/**
 * Faylni serverdan olib, brauzerga yuklatadi.
 *
 * ⚠️ Format AVVAL panel tomonda tekshiriladi. Sabab: `pdf` uchun server
 * 400 qaytaradi va bu to'g'ri, lekin operator undan oldin butun so'rovni
 * (va katta hisobot uchun uzoq kutishni) boshdan kechirardi. Yo'q javobni
 * bir soniyada berish yaxshiroq.
 */
export async function downloadServerExport(request: ServerExportRequest): Promise<void> {
  const format = parseExportFormat(request.format)
  if (format === null) {
    throw new ExportFormatError(request.format)
  }

  try {
    const response = await api.get<Blob>(request.path, {
      responseType: 'blob',
      params: { ...request.params, format },
      ...(request.signal ? { signal: request.signal } : {}),
      ...(request.onProgress
        ? {
            onDownloadProgress: (event) => {
              // ⚠️ `total` KO'PINCHA yo'q: server javobni OQIM bilan
              // yozadi, ya'ni uzunligini oldindan bilmaydi. Shuning uchun
              // foiz emas, yuklangan hajm ko'rsatiladi — soxta foiz
              // "97% da qotib qoldi" degan taassurot berardi.
              request.onProgress?.(event.loaded, event.total ?? null)
            },
          }
        : {}),
      // ⚠️ Katta eksport odatiy so'rovdan uzoq ketadi: 200 000 qator
      // XLSX bir necha daqiqa bo'lishi mumkin. Umumiy `API_TIMEOUT_MS`
      // uni yarim yo'lda uzardi va operator sababsiz xato ko'rardi.
      timeout: 0,
    })

    const name = filenameFromDisposition(
      // Axios sarlavha nomlarini kichik harfda beradi
      String(response.headers['content-disposition'] ?? ''),
      `${request.fallbackBaseName}.${format}`,
    )
    downloadBlob(response.data, name)
  } catch (error) {
    throw await unwrapBlobError(error)
  }
}

/**
 * PDF rad javobini tanib oladi.
 *
 * <h2>⚠️ Nega MATN bo'yicha — bu loyihada odatda taqiqlangan</h2>
 * Qoida: shart `tag` bo'yicha yozilsin, matn bo'yicha emas (matn
 * o'zgaradi va tarjima qilinadi). Bu yerda istisno ATAYLAB va sababi
 * o'lchangan: backend `ExportFormat.fromSlug` oddiy
 * `IllegalArgumentException` tashlaydi, servis uni `XsibErrors.badRequest`
 * bilan qaytaradi va tag `BAD_REQUEST` bo'ladi — ya'ni "noma'lum format"
 * uchun ALOHIDA tag YO'Q.
 *
 * Shuning uchun ikki qavat ishlatiladi:
 *   · asosiysi — panel `pdf` ni SERVERGA UMUMAN YUBORMAYDI
 *     ({@link ExportFormatError}), ya'ni odatiy oqimda bu funksiya
 *     kerak ham bo'lmaydi;
 *   · bu yerdagisi — zaxira: havola orqali yoki eski panel nusxasidan
 *     kelgan `format=pdf` uchun.
 *
 * Backend matni o'zgarsa panel oddiy 400 xabariga tushadi — ya'ni
 * pasayish ZARARSIZ, yolg'on xabar paydo bo'lmaydi.
 */
export function looksLikePdfRefusal(error: unknown): boolean {
  if (error instanceof ExportFormatError) {
    return error.requested.trim().toLowerCase() === 'pdf'
  }
  if (!axios.isAxiosError(error)) return false
  if (error.response?.status !== 400) return false
  const body = error.response.data as { error?: { text?: string | null } | null } | undefined
  const text = body?.error?.text ?? ''
  return /pdf/i.test(text)
}

/**
 * Eksport xatosini ekranga chiqadigan matnga aylantiradi.
 *
 * Uch holat, shu tartibda:
 *   1. PDF — sababi bilan, chunki bu "xato" emas, QAROR;
 *   2. boshqa noma'lum format — mumkin bo'lgan ro'yxat bilan;
 *   3. qolgani — panelning umumiy `translateError` i.
 */
export function translateExportError(error: unknown, t: Translate): string {
  if (looksLikePdfRefusal(error)) {
    return t('export.pdfUnavailable')
  }
  if (error instanceof ExportFormatError) {
    return t('export.formatUnknown', { formats: EXPORT_FORMATS.join(', ') })
  }
  return translateError(error, t)
}
